import { useEffect, useState } from 'react';
import rison from 'rison';
import {
  deleteDashboardFavoriteAPI,
  getDashboardIsFavoritesAPI,
  getDashboardListAPI,
  postDashboardFavoriteAPI,
} from '../../../api/dashboard';
import moment from 'moment/moment';
import { message } from 'antd';
import { useHistory } from 'react-router-dom';
import ListView from '../components/ListView';
import handleResourceExport from '../../../utils/export';
import DeleteModal from '../../../components/DeleteModal';
import { SupersetClient, t } from '@aibi-ui/core';
import {
  createErrorHandler,
  handleDashboardDelete,
} from '../../../views/CRUD/utils';
import { useToasts } from '../../../components/MessageToasts/withToasts';
import PropertiesModal from '../../../dashboard/components/PropertiesModal';

const DashboardView = ({ user }) => {
  const [query, setQuery] = useState({
    page: 0,
    page_size: 6,
    filters: [],
  });
  const [activeTab, setActiveTab] = useState(1);
  const tabs = [
    { label: '最近更新', value: 1 },
    { label: '我的收藏', value: 2 },
  ];
  /**
   * tab改变
   * @param tab
   */
  const tabChange = tab => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    if (tab === 1) {
      setQuery({ ...query, filters: [] });
    }
    if (tab === 2) {
      setQuery({
        ...query,
        filters: [
          {
            col: 'id',
            opr: 'dashboard_is_favorite',
            value: true,
          },
        ],
      });
    }
  };
  const [type, setType] = useState('list');
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  /**
   * 获取看板列表
   * @returns {Promise<void>}
   */
  const getList = async () => {
    const q = rison.encode({
      order_column: 'changed_on_delta_humanized',
      order_direction: 'desc',
      ...query,
    });
    const {
      json: { count, result },
    } = await getDashboardListAPI({ q });
    const list = result.map(r => ({
      ...r,
      title: r.dashboard_title,
      date: moment(r.changed_on_utc).format('YYYY-MM-DD HH:mm'),
    }));
    const newList = await getFavorite(list);
    setList(newList);
    setTotal(count);
  };
  /**
   * 获取看板是否喜欢
   * @returns {Promise<[]>}
   */
  const getFavorite = async list => {
    if (list.length === 0) return [];
    const q = rison.encode(list.map(r => r.id));
    const {
      json: { result },
    } = await getDashboardIsFavoritesAPI({ q });
    return list.map(r => {
      let favorite = false;
      const target = result.find(x => r.id === x.id);
      if (target) {
        favorite = target.value;
      }
      return { ...r, favorite };
    });
  };
  /**
   * 收藏 || 取消收藏
   * @param dashboard
   * @param favorite
   */
  const onFavorite = async (dashboard, favorite) => {
    if (favorite) {
      await postDashboardFavoriteAPI(dashboard.id);
      message.success('已收藏看板');
    } else {
      await deleteDashboardFavoriteAPI(dashboard.id);
      message.success('已取消收藏看板');
    }
    const newList = list.map(r => {
      if (r.id === dashboard.id) {
        r.favorite = favorite;
      }
      return r;
    });
    setList(newList);
  };
  useEffect(() => {
    getList();
  }, [query.page, query.page_size, query.filters]);

  const history = useHistory();
  /**
   * 查看更多
   */
  const onMore = () => {
    history.push('/dashboard/list/');
  };
  /**
   * 新建看板
   */
  const onAdd = () => {
    window.location.assign('/dashboard/new');
  };
  /**
   * 查看详情
   * @param dashboard
   */
  const onDetail = dashboard => {
    history.push(`/aibi/dashboard/${dashboard.id}/`);
  };
  useEffect(() => {
    if (type === 'card') {
      setQuery({ ...query, page_size: 6 });
    }
    if (type === 'list') {
      setQuery({ ...query, page_size: 6 });
    }
  }, [type]);

  const [dashboardToEdit, setDashboardToEdit] = useState(null);
  /**
   * 打开编辑
   * @param dashboard
   */
  const openDashboardEditModal = dashboard => {
    setDashboardToEdit(dashboard);
  };
  /**
   * 编辑
   * @param edits
   * @returns {Promise<void | void>}
   */
  const handleDashboardEdit = edits => {
    return SupersetClient.get({
      endpoint: `/api/v1/dashboard/${edits.id}`,
    }).then(
      ({ json = {} }) => {
        getList();
      },
      createErrorHandler(errMsg =>
        addDangerToast(
          t('An error occurred while fetching dashboards: %s', errMsg),
        ),
      ),
    );
  };
  /**
   * 导出
   * @param dashboardsToExport
   */
  const onExport = dashboardsToExport => {
    const ids = dashboardsToExport.map(({ id }) => id);
    handleResourceExport('dashboard', ids, () => {});
  };
  // 删除
  const [dashboardToDelete, setDashboardToDelete] = useState(null);
  const { addSuccessToast, addDangerToast } = useToasts();

  const actions = ['编辑', '导出', '删除'];
  /**
   * 编辑 || 导出 || 删除
   * @param item
   * @param key
   */
  const onAction = (item, key) => {
    switch (key) {
      case '编辑':
        openDashboardEditModal(item);
        return;
      case '导出':
        onExport([item]);
        return;
      case '删除':
        setDashboardToDelete(item);
        return;
      default:
        break;
    }
  };
  return (
    <>
      <ListView
        title="看板"
        activeTab={activeTab}
        tabs={tabs}
        tabChange={tabChange}
        moreText="查看所有"
        onMore={onMore}
        addText="新建看板"
        onAdd={onAdd}
        list={list}
        type={type}
        onTypeChange={type => setType(type)}
        onDetail={onDetail}
        current={query.page + 1}
        pageSize={query.page_size}
        total={total}
        onPageChange={page => setQuery({ ...query, page: page - 1 })}
        onFavorite={onFavorite}
        actions={actions}
        onAction={onAction}
      />
      {dashboardToEdit && (
        <PropertiesModal
          dashboardId={dashboardToEdit.id}
          show
          onHide={() => setDashboardToEdit(null)}
          onSubmit={handleDashboardEdit}
        />
      )}
      {dashboardToDelete && (
        <DeleteModal
          description={
            <>
              {t('Are you sure you want to delete')}{' '}
              <b>{dashboardToDelete.dashboard_title}</b>?
            </>
          }
          onConfirm={() => {
            handleDashboardDelete(
              dashboardToDelete,
              getList,
              addSuccessToast,
              addDangerToast,
              undefined,
              user?.userId,
            );
            setDashboardToDelete(null);
          }}
          onHide={() => setDashboardToDelete(null)}
          open={!!dashboardToDelete}
          title={t('Please confirm')}
        />
      )}
    </>
  );
};

export default DashboardView;
